import {
  filtrosParaConsulta,
  opcoesDeFiltroSchema,
  paginaDeHistoricoDeContatosSchema,
  paginaDeOportunidadesSchema,
  resumoDoDashboardSchema,
  type FiltroDeCampo,
  type ItemDoFunilNoCrm,
  type OpcaoDeFiltro,
  type PaginaDeHistoricoDeContatos,
  type PaginaDeOportunidades,
} from "@base/contracts";
import type { ClienteHttp } from "../../../shared/http/cliente-http";
import { ErroDaApi } from "../../../shared/http/erro-da-api";
import { paraDataIso, type Intervalo } from "../../../shared/periodo/periodos";
import type { DashboardGateway, ResultadoDoDashboard } from "./dashboard.gateway";
import { HISTORICO_VAZIO, PAGINA_VAZIA, resumoZerado } from "./dashboard.gateway.vazio";

/**
 * Numeros reais da API. Sem Kommo mapeado a API devolve zeros; se ainda
 * responder `crm-nao-configurado`, o front faz o mesmo — nunca cai em exemplo.
 */
export class DashboardGatewayHttp implements DashboardGateway {
  constructor(private readonly http: ClienteHttp) {}

  async obter(
    clienteId: string,
    periodo: Intervalo,
    filtros: readonly FiltroDeCampo[],
  ): Promise<ResultadoDoDashboard> {
    try {
      const resumo = await this.http.requisitar(
        `/v1/clientes/${encodeURIComponent(clienteId)}/dashboard`,
        resumoDoDashboardSchema,
        {
          consulta: {
            inicio: paraDataIso(periodo.inicio),
            fim: paraDataIso(periodo.fim),
            filtro: filtrosParaConsulta(filtros),
          },
        },
      );
      return { resumo, ehExemplo: false };
    } catch (erro) {
      if (erro instanceof ErroDaApi && erro.problema?.type.endsWith("/crm-nao-configurado")) {
        return { resumo: resumoZerado(periodo), ehExemplo: false };
      }
      throw erro;
    }
  }

  async opcoesDeFiltro(clienteId: string): Promise<readonly OpcaoDeFiltro[]> {
    const resposta = await this.http.requisitar(
      `/v1/clientes/${encodeURIComponent(clienteId)}/dashboard/filtros`,
      opcoesDeFiltroSchema,
    );
    return resposta.dados;
  }

  async oportunidades(
    clienteId: string,
    periodo: Intervalo,
    filtros: readonly FiltroDeCampo[],
    etapa: ItemDoFunilNoCrm,
    cursor?: string,
  ): Promise<PaginaDeOportunidades> {
    try {
      return await this.http.requisitar(
        `/v1/clientes/${encodeURIComponent(clienteId)}/dashboard/oportunidades`,
        paginaDeOportunidadesSchema,
        {
          consulta: {
            inicio: paraDataIso(periodo.inicio),
            fim: paraDataIso(periodo.fim),
            etapa,
            filtro: filtrosParaConsulta(filtros),
            ...(cursor ? { cursor } : {}),
          },
        },
      );
    } catch (erro) {
      if (erro instanceof ErroDaApi && erro.problema?.type.endsWith("/crm-nao-configurado")) {
        return PAGINA_VAZIA;
      }
      throw erro;
    }
  }

  async historicoDeContatos(
    clienteId: string,
    periodo: Intervalo,
    filtros: readonly FiltroDeCampo[],
    cursor?: string,
  ): Promise<PaginaDeHistoricoDeContatos> {
    try {
      return await this.http.requisitar(
        `/v1/clientes/${encodeURIComponent(clienteId)}/dashboard/contatos`,
        paginaDeHistoricoDeContatosSchema,
        {
          consulta: {
            inicio: paraDataIso(periodo.inicio),
            fim: paraDataIso(periodo.fim),
            filtro: filtrosParaConsulta(filtros),
            ...(cursor ? { cursor } : {}),
          },
        },
      );
    } catch (erro) {
      if (erro instanceof ErroDaApi && erro.problema?.type.endsWith("/crm-nao-configurado")) {
        return HISTORICO_VAZIO;
      }
      throw erro;
    }
  }
}
